<template>
  <div v-if="loading">Loading...</div>
  <div v-else class="tl-page">
    <div class="tl-header-bar">
      <h1 class="tl-title">{{ tour.attributes.tourName }}</h1>
      <div class="tl-header-actions">
        <router-link :to="{ path: '/calculation/tourlist' }">
          <button class="tl-btn tl-btn--ghost">← Back to List</button>
        </router-link>
      </div>
    </div>

    <div class="tl-info-card">
      <div class="tl-info-grid">
        <div v-for="(label, key) in details" :key="key" class="tl-info-item">
          <strong>{{ label }}</strong>
          <p>{{ tour.attributes[key] }}</p>
        </div>

        <!-- Cities -->
        <div class="tl-info-item">
          <strong>Cities</strong>
          <p>
            <span v-for="(city, idx) in tour.attributes.cities.data" :key="city.id">
              {{ city.attributes.city }}{{ idx < tour.attributes.cities.data.length - 1 ? ', ' : '' }} </span>
          </p>
        </div>

        <!-- Port -->
        <div class="tl-info-item">
          <strong>Port City</strong>
          <p>{{ tour.attributes.port?.data?.attributes?.city || 'N/A' }}</p>
        </div>
      </div>

      <div class="tl-textareas-grid mt-4">
        <!-- Timing & Description -->
        <div class="tl-textarea-item" v-for="(text, key) in texts" :key="key">
          <strong>{{ text }}</strong>
          <textarea class="tl-textarea confined-textarea" v-model="tour.attributes[key]"
            @blur="saveTimingDescription(key)">
          </textarea>
        </div>

        <!-- Comments -->
        <div class="tl-textarea-item tl-textarea-item--full">
          <strong>Comments</strong>
          <div class="position-relative">
            <textarea class="tl-textarea tl-textarea--success" v-model="tour.attributes.comments" @blur="saveComments">
            </textarea>
            <i class="fas fa-check text-success tl-textarea-icon"></i>
          </div>
        </div>

        <!-- Remarks -->
        <div class="tl-textarea-item tl-textarea-item--full" v-if="tour.attributes.remarks">
          <strong>Remarks</strong>
          <textarea class="tl-textarea tl-textarea--danger" v-model="tour.attributes.remarks" @input="updateRemarks">
          </textarea>
        </div>
      </div>
    </div>

    <!-- Service Modal -->
    <b-modal ref="serviceModal" id="service-modal" title="Add New Service" @ok="addService" centered
      header-class="tl-modal-header" body-class="tl-modal-body" footer-class="tl-modal-footer">
      <div class="tl-form-group">
        <label class="tl-label" for="serviceType">Select Type</label>
        <select v-model="selectedType" id="serviceType" class="tl-select tl-select--full">
          <option :value="null" disabled>Select a type</option>
          <option v-for="type in serviceTypes" :key="type" :value="type"> {{ type }}</option>
        </select>
      </div>

      <div class="tl-form-group mt-4" v-if="filteredServices.length || selectedType">
        <label class="tl-label" for="newService">Select Service</label>
        <select v-model="selectedService" id="newService" class="tl-select tl-select--full">
          <option :value="null" disabled>Select a service</option>
          <option v-for="service in filteredServices" :key="service.id" :value="service.id">
            {{ service.attributes.item }} - ID {{ service.id }}
          </option>
        </select>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <button class="tl-btn tl-btn--ghost" @click="cancel()">Cancel</button>
        <button class="tl-btn tl-btn--primary" @click="ok()" :disabled="!selectedService">OK</button>
      </template>
    </b-modal>

    <!-- Remove Confirmation Modal -->
    <b-modal ref="removeConfirmModal" id="remove-confirm-modal" title="Confirm Deletion" @ok="confirmRemoveService"
      centered header-class="tl-modal-header" body-class="tl-modal-body" footer-class="tl-modal-footer">
      <div class="text-center py-3">
        <div class="mb-3 text-danger" style="font-size: 2.5rem;">
          <i class="fas fa-exclamation-circle"></i>
        </div>
        <h5 class="font-weight-bold mb-2">Confirm Deletion</h5>
        <p class="text-muted mb-0">Are you sure you want to remove this item from the tour?</p>
        <p class="small text-danger mt-2">This action cannot be undone.</p>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <button class="tl-btn tl-btn--ghost" @click="cancel()">Cancel</button>
        <button class="tl-btn tl-btn--danger" @click="ok()">Delete Item</button>
      </template>
    </b-modal>

    <!-- Tables for Different Item Types -->
    <div class="mt-4">
      <div class="tl-section-header">
        <h2 class="tl-section-title">Items by Type</h2>
        <button class="tl-btn tl-btn--primary" v-b-tooltip.hover title="Add Services" @click="showModal">
          + Add Service
        </button>
      </div>

      <div class="tl-tables-container">
        <!-- Table for Per Person -->
        <div v-if="filteredItems('per person').length" class="tl-table-group">
          <div class="tl-banner tl-banner--person">Per Person</div>
          <div class="tl-table-card">
            <div class="tl-table-scroll">
              <table class="tl-table tl-table--person">
                <thead>
                  <tr>
                    <th class="tl-th">items</th>
                    <th v-for="key in filteredItemKeys('per person')" :key="key" class="tl-th">{{ key }}</th>
                    <th class="tl-th">action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in filteredItems('per person')" :key="item.id" class="tl-row">
                    <td class="tl-td tl-td--name">{{ item.attributes.item }}</td>
                    <td v-for="key in filteredItemKeys('per person')" :key="key" class="tl-td">
                      <span v-if="key === 'city'">
                        {{ item.attributes.city?.data?.attributes?.city || 'N/A' }}
                      </span>
                      <span v-else>
                        <span
                          v-if="['adultPrice_gross', 'adultPrice_net', 'childPrice_gross', 'childPrice_net', 'price'].includes(key)">
                          {{ formatPrice(item.attributes[key], city) }}
                        </span>
                        <span v-else>
                          {{ item.attributes[key] }}
                        </span>
                      </span>
                    </td>
                    <td class="tl-td">
                      <button class="tl-icon-btn tl-icon-btn--danger" v-b-tooltip.hover title="Remove Service"
                        @click="showRemoveConfirm(item.id)">
                        🗑
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Table for Bulk -->
        <div v-if="filteredItems('bulk').length" class="tl-table-group mt-4">
          <div class="tl-banner tl-banner--bulk">Bulk</div>
          <div class="tl-table-card">
            <div class="tl-table-scroll">
              <table class="tl-table tl-table--bulk">
                <thead>
                  <tr>
                    <th class="tl-th">items</th>
                    <th v-for="key in filteredItemKeys('bulk')" :key="key" class="tl-th">{{ key }}</th>
                    <th class="tl-th">factor</th>
                    <th class="tl-th">calculated price</th>
                    <th class="tl-th">action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in filteredItems('bulk')" :key="item.id" class="tl-row">
                    <td class="tl-td tl-td--name">{{ item.attributes.item }}</td>
                    <td v-for="key in filteredItemKeys('bulk')" :key="key" class="tl-td">
                      <span v-if="key === 'city'">
                        {{ item.attributes.city?.data?.attributes?.city || 'N/A' }}
                      </span>
                      <span v-else>
                        <span
                          v-if="['adultPrice_gross', 'adultPrice_net', 'childPrice_gross', 'childPrice_net', 'price'].includes(key)">
                          {{ formatPrice(item.attributes[key], city) }}
                        </span>
                        <span v-else>
                          {{ item.attributes[key] }}
                        </span>
                      </span>
                    </td>
                    <td class="tl-td">
                      <input type="number" step="0.01" v-model="itemFactors[item.id]" class="tl-input tl-input--sm"
                        @input="updateItemFactor(item.id, itemFactors[item.id])" />
                    </td>
                    <td class="tl-td">
                      <span v-if="item.attributes.price" class="tl-price-highlight">
                        {{ formatPrice(item.attributes.price * itemFactors[item.id], city) }}
                      </span>
                    </td>
                    <td class="tl-td">
                      <button class="tl-icon-btn tl-icon-btn--danger" v-b-tooltip.hover title="Remove Service"
                        @click="showRemoveConfirm(item.id)">
                        🗑
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Table for Hourly -->
        <div v-if="filteredItems('hourly').length" class="tl-table-group mt-4">
          <div class="tl-banner tl-banner--hourly">Hourly</div>
          <div class="tl-table-card">
            <div class="tl-table-scroll">
              <table class="tl-table tl-table--hourly">
                <thead>
                  <tr>
                    <th class="tl-th">items</th>
                    <th v-for="key in filteredItemKeys('hourly')" :key="key" class="tl-th">{{ key }}</th>
                    <th class="tl-th">factor</th>
                    <th class="tl-th">calculated price</th>
                    <th class="tl-th">action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in filteredItems('hourly')" :key="item.id" class="tl-row">
                    <td class="tl-td tl-td--name">{{ item.attributes.item }}</td>
                    <td v-for="key in filteredItemKeys('hourly')" :key="key" class="tl-td">
                      <span v-if="key === 'city'">
                        {{ item.attributes.city?.data?.attributes?.city || 'N/A' }}
                      </span>
                      <span v-else>
                        <span
                          v-if="['adultPrice_gross', 'adultPrice_net', 'childPrice_gross', 'childPrice_net', 'price'].includes(key)">
                          {{ formatPrice(item.attributes[key], city) }}
                        </span>
                        <span v-else>
                          {{ item.attributes[key] }}
                        </span>
                      </span>
                    </td>
                    <td class="tl-td">
                      <input type="number" step="0.01" v-model="itemFactors[item.id]" class="tl-input tl-input--sm"
                        @input="updateItemFactor(item.id, itemFactors[item.id])" />
                    </td>
                    <td class="tl-td">
                      <span v-if="item.attributes.price" class="tl-price-highlight">
                        {{ formatPrice(item.attributes.price * itemFactors[item.id], city) }}
                      </span>
                    </td>
                    <td class="tl-td">
                      <button class="tl-icon-btn tl-icon-btn--danger" v-b-tooltip.hover title="Remove Service"
                        @click="showRemoveConfirm(item.id)">
                        🗑
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- No Items Available Message -->
        <div
          v-if="!filteredItems('per person').length && !filteredItems('bulk').length && !filteredItems('hourly').length"
          class="tl-empty-state">
          <p>No items available for this tour.</p>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { formatPrice } from '../Helper/PriceDisplay.js';

export default {
  data() {
    return {
      tour: {},
      loading: true,
      availableServices: [],
      selectedService: null,
      selectedType: null,
      serviceTypes: [],
      filteredServices: [],
      details: {
        tourName: 'Tour Name',
        tourCode: 'Tour Code',
        duration: 'Duration',
        paxMin: 'Min Pax',
        paxMax: 'Max Pax',
        cruiseLine: 'Cruise Line'
      },
      texts: {
        timing: 'Timings',
        description: 'Description'
      },
      itemKeys: [
        'tax',
        'rate',
        'catagory',
        'remarks',
        'supplier',
        'adultPrice_gross',
        'adultPrice_net',
        'childPrice_gross',
        'childPrice_net',
        'price',
      ],
      serviceToRemove: null,
      itemFactors: {},
      remarks: ''
    };
  },
  async created() {
    const tourId = this.$route.params.id;

    // Fetch tour data
    const tourResponse = await this.$xhr.get(`/api/tours/${tourId}?populate[item][populate]=*&populate[cities][populate]=*&populate[port][populate]=*`);
    this.tour = tourResponse.data.data;
    this.itemFactors = this.tour.attributes.itemFactor || {}; // Initialize itemFactors

    // Fetch services with backend filtering
    const tourYear = this.tour.attributes.year;
    const tourCities = this.tour.attributes.cities?.data?.map(city => city.attributes?.city) || [];

    // Fetch all matching services using pagination loops
    let allServices = [];
    let pageNum = 1;
    let pageSize = 500;
    let totalFound = 0;
    let hasMore = true;

    const queryParams = new URLSearchParams();
    queryParams.append('populate', '*');
    queryParams.append('pagination[pageSize]', pageSize);
    queryParams.append('filters[year][$eq]', tourYear);

    if (tourCities.length > 0) {
      tourCities.forEach((city) => {
        queryParams.append('filters[cities][city][$in]', city);
      });
    }

    console.log('[TourTable] Starting paginated fetch for matches...');
    console.log('[TourTable] Query Params:', queryParams.toString());

    try {
      while (hasMore) {
        queryParams.set('pagination[page]', pageNum);
        const response = await this.$xhr.get(`/api/services?${queryParams.toString()}`);
        const data = response.data.data;
        const meta = response.data.meta;

        allServices = allServices.concat(data);
        totalFound = meta.pagination.total;

        console.log(`[TourTable] Loaded page ${pageNum}, items: ${allServices.length}/${totalFound}`);

        if (allServices.length >= totalFound || data.length < pageSize) {
          hasMore = false;
        } else {
          pageNum++;
        }
      }
    } catch (error) {
      console.error('[TourTable] Error fetching services:', error);
      if (error.response) {
        console.error('[TourTable] Error data:', error.response.data);
      }
      this.$bvToast.toast('Failed to load available services.', { variant: 'danger' });
    }

    this.availableServices = allServices;
    console.log('[TourTable] Final available services count:', this.availableServices.length);

    // Populate service types
    this.serviceTypes = [...new Set(this.availableServices.map(service => service.attributes.type))].filter(Boolean).sort();
    console.log('[TourTable] Service Types available:', this.serviceTypes);

    this.loading = false;
  },
  computed: {
    city() {
      const cities = this.tour?.attributes?.cities?.data;
      if (!cities || cities.length === 0) return null;
      const prague = cities.find(c => c.attributes.city === 'Prague');
      return prague ? 'Prague' : cities[0].attributes.city;
    }
  },
  watch: {
    selectedType(newType) {
      this.filteredServices = this.availableServices
        .filter(service => service.attributes.type === newType)
        .sort((a, b) => a.attributes.item.localeCompare(b.attributes.item)); // Sorting alphabetically
      this.selectedService = null;
    }
  },
  methods: {
    formatPrice,
    filteredItemKeys(type) {
      if (type === 'per person') {
        return this.itemKeys.filter(key => !['price'].includes(key));
      } else {
        return this.itemKeys.filter(key => !['adultPrice_net', 'childPrice_net', 'adultPrice_gross', 'childPrice_gross'].includes(key));
      }
    },
    async addService() {
      console.log('[TourTable] Trying to add service ID:', this.selectedService);
      if (this.selectedService) {
        const newService = await this.$xhr.get(`/api/services/${this.selectedService}?populate=*`).then(res => res.data.data);
        this.tour.attributes.item.data.push(newService);

        if (!newService.attributes.itemFactor) {
          this.itemFactors[newService.id] = 1;
        } else {
          this.itemFactors[newService.id] = newService.attributes.itemFactor;
        }

        await this.$xhr.put(`/api/tours/${this.tour.id}`, {
          data: {
            item: this.tour.attributes.item.data.map(service => ({ id: service.id })),
            itemFactor: this.itemFactors
          }
        });
        this.selectedService = null;
        this.selectedType = null;

        this.$bvToast.toast('Item added successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 5000
        });
      }
    },

    showRemoveConfirm(serviceId) {
      this.serviceToRemove = serviceId;
      this.$refs.removeConfirmModal.show();
    },
    async confirmRemoveService() {
      this.tour.attributes.item.data = this.tour.attributes.item.data.filter(item => item.id !== this.serviceToRemove);
      await this.$xhr.put(`/api/tours/${this.tour.id}`, {
        data: { item: this.tour.attributes.item.data.map(service => ({ id: service.id })) }
      });

      this.$bvToast.toast('Item removed successfully!', {
        title: 'Success',
        variant: 'success',
        solid: true,
        autoHideDelay: 5000
      });

      this.serviceToRemove = null;  // Reset after removal
    },
    async updateItemFactor(serviceId, newValue) {
      // Update local state
      this.itemFactors[serviceId] = newValue;
      await this.$xhr.put(`/api/tours/${this.tour.id}`, {
        data: { itemFactor: this.itemFactors }
      });
      this.$bvToast.toast('Item factor updated successfully!', {
        title: 'Success',
        variant: 'success',
        solid: true,
        autoHideDelay: 5000
      });
    },
    async updateRemarks() {
      // Update the remarks field in the backend
      await this.$xhr.put(`/api/tours/${this.tour.id}`, {
        data: { remarks: this.tour.attributes.remarks }
      });
      this.$bvToast.toast('Remarks updated successfully!', {
        title: 'Success',
        variant: 'success',
        solid: true,
        autoHideDelay: 5000
      });
    },
    async saveComments() {
      try {
        await this.$xhr.put(`/api/tours/${this.tour.id}`, {
          data: {
            comments: this.tour.attributes.comments,
          },
        });
        this.$bvToast.toast('Comments saved successfully.', { variant: 'success' });
      } catch (error) {
        this.$bvToast.toast('Failed to save comments.', { variant: 'danger' });
      }
    },
    async saveTimingDescription(key) {
      try {
        await this.$xhr.put(`/api/tours/${this.tour.id}`, {
          data: {
            [key]: this.tour.attributes[key],
          },
        });
        this.$bvToast.toast(`${key} saved successfully.`, { variant: 'success' });
      } catch (error) {
        this.$bvToast.toast(`Failed to save ${key}.`, { variant: 'danger' });
      }
    },
    filteredItems(type) {
      return this.tour.attributes?.item?.data?.filter(item => item.attributes.type === type) || [];
    },
    showModal() {
      this.$refs.serviceModal.show();
    }
  }
};
</script>



<style scoped>
/* ────────────── Page & General ────────────── */
.tl-page {
  padding: 16px 20px;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
  background: #f8fafc;
  min-height: 100vh;
}

.tl-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 12px 20px;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.tl-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #111827;
  margin: 0;
}

.tl-section-header {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
  margin-bottom: 24px;
}

.tl-section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  text-align: center;
}

.tl-section-header .tl-btn {
  position: absolute;
  right: 4px;
}

/* ────────────── Info Card ────────────── */
.tl-info-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.tl-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 24px;
}

.tl-info-item strong {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.tl-info-item p {
  font-weight: 600;
  color: #334155;
  margin: 0;
  font-size: 0.95rem;
}

/* ────────────── Textareas ────────────── */
.tl-textareas-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.tl-textarea-item--full {
  grid-column: span 2;
}

.tl-textarea-item strong {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}

.tl-textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;
  font-size: 0.9rem;
  line-height: 1.5;
  outline: none;
  transition: all 0.2s;
  background: #f8fafc;
}

.tl-textarea:focus {
  border-color: #E2923D;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, 0.1);
}

.tl-textarea--success {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.tl-textarea--success:focus {
  border-color: #22c55e;
}

.tl-textarea--danger {
  background: #fef2f2;
  border-color: #fecaca;
}

.tl-textarea--danger:focus {
  border-color: #ef4444;
}

.tl-textarea-icon {
  position: absolute;
  top: 12px;
  right: 12px;
  pointer-events: none;
}

.confined-textarea {
  height: 12rem;
}

/* ────────────── Banners ────────────── */
.tl-banner {
  padding: 10px 16px;
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.tl-banner--person {
  background: #E2923D;
}

.tl-banner--bulk {
  background: #3B82F6;
}

.tl-banner--hourly {
  background: #8B5CF6;
}

/* ────────────── Table Card ────────────── */
.tl-table-card {
  border: 1px solid #e9ecef;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .06);
  background: #fff;
}

.tl-table-scroll {
  overflow-x: auto;
}

/* ────────────── Table ────────────── */
.tl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .82rem;
  white-space: nowrap;
}

/* Head */
.tl-table thead tr {
  background: #f9fafb;
}

.tl-table--person thead tr {
  background: #fffaf5;
}

.tl-table--bulk thead tr {
  background: #f5f9ff;
}

.tl-table--hourly thead tr {
  background: #faf9ff;
}

.tl-th {
  padding: 12px 14px;
  text-align: center;
  color: #64748b;
  font-weight: 700;
  font-size: .68rem;
  text-transform: uppercase;
  letter-spacing: .05em;
  border: none;
}

/* Body */
.tl-td {
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  text-align: center;
  vertical-align: middle;
}

.tl-td--name {
  text-align: left;
  font-weight: 600;
  color: #1e293b;
  max-width: 250px;
  white-space: normal;
}

.tl-row:hover .tl-td {
  background: #f8fafc;
}

/* ────────────── Components ────────────── */
.tl-icon-btn {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all .12s;
  font-size: 0.9rem;
}

.tl-icon-btn:hover {
  background: #f9fafb;
  border-color: #E2923D;
  transform: scale(1.05);
}

.tl-icon-btn--danger:hover {
  background: #fee2e2;
  border-color: #ef4444;
}

.tl-btn {
  height: 32px;
  padding: 0 14px;
  font-size: .8rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all .15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.tl-btn--primary {
  background: #E2923D;
  color: #fff;
}

.tl-btn--danger {
  background: #ef4444;
  color: #fff;
}

.tl-btn--ghost {
  background: #f1f5f9;
  color: #475569;
}

.tl-btn--sm {
  height: 28px;
  padding: 0 10px;
  font-size: .75rem;
}

.tl-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.tl-input {
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: .85rem;
  outline: none;
  transition: border-color .15s;
}

.tl-input:focus {
  border-color: #E2923D;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .1);
}

.tl-input--sm {
  width: 80px;
  text-align: center;
}

.tl-price-highlight {
  font-weight: 700;
  color: #059669;
}

/* ────────────── Modal Enhancements ────────────── */
:deep(.tl-modal-header) {
  border-bottom: 1px solid #f1f5f9;
  padding: 20px 24px;
}

:deep(.tl-modal-header .modal-title) {
  font-size: 1.1rem;
  font-weight: 800;
  color: #111827;
}

:deep(.tl-modal-body) {
  padding: 24px;
}

:deep(.tl-modal-footer) {
  border-top: 1px solid #f1f5f9;
  padding: 16px 24px;
  display: flex !important;
  justify-content: flex-end;
  gap: 12px;
}

.tl-form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.tl-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.05em;
  margin: 0;
  text-align: center;
}

.tl-select {
  height: 38px;
  width: 100%;
  font-size: .9rem;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #f8fafc;
  color: #374151;
  cursor: pointer;
  outline: none;
  transition: all .2s;
}

.tl-select:focus {
  border-color: #E2923D;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .15);
}

.tl-select--full {
  width: 100%;
}

.tl-empty-state {
  padding: 40px;
  text-align: center;
  color: #64748b;
  background: #fff;
  border: 2px dashed #e2e8f0;
  border-radius: 10px;
  margin-top: 16px;
}

/* Utility */
.position-relative {
  position: relative;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}
</style>